import { useState, useEffect, useCallback, useRef } from 'react';
import { InfoClient, SubscriptionClient } from '../sdk';
import { ConnectionState } from '../sdk/types/transport';

/**
 * Retry a function with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param initialDelay - Initial delay in ms (default: 1000)
 * @returns Promise that resolves with the function result
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError || new Error("Retry failed");
}

export interface PendingOrdersConfig {
  baseURL: string;
  wsURL?: string; // WebSocket URL (required if useWebSocket is true)
  authToken?: string;
  walletAddress?: string; // Optional - if not provided, API may derive from auth token
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
}

export interface PendingOrder {
  id?: string;
  orderId?: string;
  marketId?: string;
  symbol?: string;
  side?: 'BUY' | 'SELL' | 'LONG' | 'SHORT';
  type?: 'LIMIT' | 'MARKET' | 'STOP' | 'STOP_LIMIT';
  quantity?: number;
  price?: number;
  filledQuantity?: number;
  remainingQuantity?: number;
  status?: string;
  timeInForce?: string;
  createdAt?: number;
  updatedAt?: number;
}

/**
 * Hook for managing pending orders data
 * Uses WebSocket as primary source (via user_updates channel) with HTTP fallback
 * Maintains backward compatibility with polling approach
 */
export function usePendingOrders(config: PendingOrdersConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);
  const isSubscribedRef = useRef<boolean>(false);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Transform API/WS response to PendingOrder format
   */
  const transformOrder = useCallback((order: any): PendingOrder => {
    return {
      id: order.id || order.orderId || order.order_id,
      orderId: order.orderId || order.order_id || order.id,
      marketId: order.marketId || order.market_id || order.assetPair,
      symbol: order.symbol || order.pair || `Market-${order.marketId || order.market_id || 'N/A'}`,
      side: order.side || (order.direction === 'LONG' ? 'LONG' : order.direction === 'SHORT' ? 'SHORT' : undefined),
      type: order.type || order.orderType || order.order_type,
      quantity: order.quantity || order.size || order.amount || 0,
      price: order.price || order.limitPrice || order.limit_price || 0,
      filledQuantity: order.filledQuantity || order.filled_quantity || order.filled || 0,
      remainingQuantity: order.remainingQuantity || order.remaining_quantity || order.remaining || 0,
      status: order.status || order.orderStatus || order.order_status || 'PENDING',
      timeInForce: order.timeInForce || order.time_in_force || order.tif,
      createdAt: order.createdAt || order.created_at || order.timestamp,
      updatedAt: order.updatedAt || order.updated_at,
    };
  }, []);

  /**
   * Fetch orders from API with retry logic
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchOrders = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch orders
    // The API requires an address parameter
    // Don't set error state if address is simply not yet loaded (empty string)
    // Only set error if we've attempted to fetch and it failed
    if (!currentConfig.walletAddress || currentConfig.walletAddress.trim() === '') {
      // Clear error and orders when address is not available
      setError(null);
      setOrders([]);
      setLoading(false);
      return;
    }

    setError(null);

    try {
      const data = await retryWithBackoff(
        () => infoClientRef.current!.getOrders(currentConfig.walletAddress!),
        3,
        1000,
      );
      
      // Handle response structure: { orders: Order[], total_count: number, timestamp: string }
      const ordersArray = Array.isArray(data) ? data : (data?.orders || []);
      
      if (Array.isArray(ordersArray)) {
        // Fetch all orders (no status filtering - component will filter based on view)
        const allOrders = ordersArray.map(transformOrder);
        // Compare with current orders to avoid unnecessary state updates
        setOrders(prev => {
          if (prev.length !== allOrders.length) {
            return allOrders;
          }
          // Deep comparison - only update if orders actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(allOrders);
          return prevStr === newStr ? prev : allOrders;
        });
      } else {
        setOrders(prev => prev.length === 0 ? prev : []);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load orders';
      setError(errorMessage);
      console.error('Failed to fetch orders after retries:', err);
    } finally {
      setLoading(false);
    }
  }, [transformOrder]); // Removed config.walletAddress to prevent loops - using configRef instead

  /**
   * Get order ID from an order object (handles various ID field names)
   */
  const getOrderId = useCallback((order: PendingOrder): string | undefined => {
    return order.id || order.orderId;
  }, []);

  /**
   * Handle order updates from WebSocket (orders channel)
   * Updates orders by order ID - merges new/updated orders and removes completed/cancelled ones
   * Handles both single order updates and arrays of orders
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleOrdersUpdate = useCallback((data: any) => {
    // Handle various data formats:
    // 1. Single order object: { id: "...", status: "...", ... }
    // 2. Array of orders: [{ id: "...", ... }, ...]
    // 3. Object with orders array: { orders: [...], count: ... }
    let ordersArray: any[] = [];
    
    if (Array.isArray(data)) {
      ordersArray = data;
    } else if (data.orders && Array.isArray(data.orders)) {
      ordersArray = data.orders;
    } else if (data && typeof data === 'object') {
      // Check if it's a single order object (has id/orderId)
      if (data.id || data.orderId || data.order_id) {
        ordersArray = [data]; // Single order update
      } else {
        // Try to extract orders from various possible structures
        ordersArray = data.order || data.orderList || [];
      }
    }
    
    if (ordersArray.length > 0 || (data && typeof data === 'object' && 'count' in data && data.count === 0)) {
      // Transform incoming orders
      const incomingOrders = ordersArray.map(transformOrder);
      
      // Update orders by merging based on order ID
      setOrders(prev => {
        // Create a map of existing orders by ID for efficient lookup
        const ordersMap = new Map<string | undefined, PendingOrder>();
        prev.forEach(order => {
          const id = getOrderId(order);
          if (id) {
            ordersMap.set(id, order);
          }
        });
        
        // Process incoming orders: update existing or add new
        incomingOrders.forEach(incomingOrder => {
          const id = getOrderId(incomingOrder);
          
          if (!id) {
            // Order without ID - log warning and skip
            console.warn('Received order without ID, skipping:', incomingOrder);
            return;
          }
          
          // If order has a status that indicates it's no longer active, remove it
          const status = incomingOrder.status?.toUpperCase();
          const isInactive = status === 'FILLED' || status === 'CANCELLED' || 
                            status === 'REJECTED' || status === 'EXPIRED' ||
                            status === 'CLOSED' || status === 'DONE';
          
          if (isInactive) {
            // Remove completed/cancelled orders
            ordersMap.delete(id);
          } else {
            // Update existing order or add new one
            ordersMap.set(id, incomingOrder);
          }
        });
        
        // Convert map back to array, filtering out any undefined IDs
        const updatedOrders = Array.from(ordersMap.values()).filter(order => getOrderId(order));
        
        // Only update if orders actually changed
        if (prev.length !== updatedOrders.length) {
          return updatedOrders;
        }
        
        // Deep comparison - only update if orders actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(updatedOrders);
        return prevStr === newStr ? prev : updatedOrders;
      });
    }
  }, [transformOrder, getOrderId]);

  // Initialize clients and set up WebSocket or polling
  useEffect(() => {
    // Skip initialization if walletAddress is not available
    // This prevents unnecessary client creation and fetch attempts
    if (!config.walletAddress || config.walletAddress.trim() === '') {
      setLoading(false);
      setError(null);
      setOrders([]);
      // Clean up any existing connections
      if (subscriptionClientRef.current) {
        subscriptionClientRef.current.disconnect();
        subscriptionClientRef.current = null;
      }
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch from HTTP (always do this for fast initial load)
    fetchOrders();

    if (useWebSocket && config.wsURL) {
      // WebSocket mode: primary source
      const subscriptionClient = new SubscriptionClient({
        url: config.wsURL,
        authToken: config.authToken,
        walletAddress: config.walletAddress,
        deviceID: config.deviceID,
      });
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        if (
          subscriptionClient.isConnected() &&
          !isSubscribedRef.current
        ) {
          // Use subscribeOrders for dedicated orders channel (more efficient than user_updates)
          subscriptionClient.subscribeOrders(handleOrdersUpdate);
          isSubscribedRef.current = true;
        }
      };

      // Connect WebSocket and subscribe
      subscriptionClient
        .connect()
        .then(() => {
          setIsConnected(true);
          subscribe();
        })
        .catch((err) => {
          console.error('Failed to connect WebSocket:', err);
          setIsConnected(false);
          // Fallback to polling if WS fails
          if (config.refreshInterval && config.refreshInterval > 0) {
            refreshIntervalRef.current = window.setInterval(() => {
              fetchOrders();
            }, config.refreshInterval);
          }
        });

      // Monitor connection state changes and re-subscribe on reconnection
      let unsubscribeStateChange: (() => void) | null = null;
      const wsClient = (subscriptionClient as any).wsClient;
      if (wsClient) {
        unsubscribeStateChange = wsClient.onStateChange(
          (state: ConnectionState) => {
            if (state === ConnectionState.Connected) {
              setIsConnected(true);
              // Re-subscribe if we were previously subscribed
              if (isSubscribedRef.current) {
                subscribe();
              }
              // Stop polling when WS is connected
              if (refreshIntervalRef.current !== null) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
              }
            } else if (
              state === ConnectionState.Disconnected ||
              state === ConnectionState.Reconnecting
            ) {
              setIsConnected(false);
              isSubscribedRef.current = false;
              // Fallback to polling when WS is disconnected
              if (config.refreshInterval && config.refreshInterval > 0) {
                if (refreshIntervalRef.current === null) {
                  refreshIntervalRef.current = window.setInterval(() => {
                    fetchOrders();
                  }, config.refreshInterval);
                }
              }
            }
          },
        );
      }

      return () => {
        // Unsubscribe from state changes
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }

        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }

        if (subscriptionClientRef.current) {
          subscriptionClientRef.current.unsubscribeOrders(handleOrdersUpdate);
          subscriptionClientRef.current.disconnect();
        }
      };
    } else {
      // Polling mode: fallback or when useWebSocket is false
      if (config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(() => {
          fetchOrders();
        }, config.refreshInterval);
      }

      return () => {
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.baseURL,
    config.wsURL,
    config.authToken,
    config.walletAddress,
    config.deviceID,
    config.refreshInterval,
    useWebSocket,
    // Note: fetchOrders and handleUserUpdates are intentionally NOT in dependencies
    // fetchOrders uses configRef to access latest config without causing dependency loops
    // handleUserUpdates is stable (only depends on transformOrder) and subscription is managed separately
    // The effect will re-run when walletAddress changes (included above), which is the desired behavior
  ]);

  return {
    orders,
    loading,
    error,
    isConnected,
    refresh: fetchOrders,
  };
}
